import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { leagues } from "@/mocks/leagues";
import { useSiteSearch } from "@/hooks/useSiteSearch";

export default function SiteHeader() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const results = useSiteSearch(query);

  const handleGo = (to: string) => {
    setSearchOpen(false);
    setQuery("");
    setMobileOpen(false);
    navigate(to);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (results.length > 0) handleGo(results[0].to);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
      isActive
        ? "text-accent-400 bg-primary-800"
        : "text-primary-100 hover:text-background-50 hover:bg-primary-800"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-primary-950 border-b border-primary-800">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 gap-3">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
            <span className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center text-background-50 dark:text-foreground-950">
              <i className="ri-basketball-fill text-xl" />
            </span>
            <span className="leading-tight">
              <span className="block font-heading font-semibold text-lg text-background-50 tracking-wide">
                篮球直播
              </span>
              <span className="block font-label text-[10px] tracking-[0.28em] text-primary-300">
                BASKETBALL LIVE
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" end className={navLinkClass}>
              首页
            </NavLink>
            <div
              className="relative"
              onMouseEnter={() => setCatOpen(true)}
              onMouseLeave={() => setCatOpen(false)}
            >
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className="flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-primary-100 hover:text-background-50 hover:bg-primary-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                赛事分类
                <i className="ri-arrow-down-s-line text-base" />
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full pt-1 w-56">
                  <div className="bg-background-50 rounded-lg border border-background-200 py-1.5 shadow-sm">
                    {leagues.map((l) => (
                      <button
                        key={l.slug}
                        type="button"
                        onClick={() => {
                          setCatOpen(false);
                          navigate(`/category/${l.slug}`);
                        }}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: l.accent }}
                        />
                        <span className="text-sm text-foreground-800">{l.name}</span>
                        <span className="ml-auto text-xs text-foreground-500">{l.region}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <NavLink to="/schedule" className={navLinkClass}>
              赛程
            </NavLink>
            <NavLink to="/news" className={navLinkClass}>
              资讯
            </NavLink>
            <NavLink to="/about" className={navLinkClass}>
              关于
            </NavLink>
          </nav>

          <div className="hidden md:block relative w-56 lg:w-72" ref={boxRef}>
            <form onSubmit={handleSubmit} className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-primary-300 text-base" />
              <input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                placeholder="搜索赛事、球队、资讯"
                className="w-full bg-primary-900 border border-primary-700 rounded-full pl-9 pr-3 py-2 text-sm text-background-50 placeholder:text-primary-400 focus:outline-none focus:border-accent-500 transition-colors"
              />
            </form>
            {searchOpen && query.trim() !== "" && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-background-50 rounded-lg border border-background-200 py-1.5 max-h-96 overflow-y-auto shadow-sm">
                {results.length === 0 ? (
                  <div className="px-4 py-6 text-center">
                    <i className="ri-search-eye-line text-2xl text-foreground-400" />
                    <p className="mt-2 text-sm text-foreground-600">
                      未找到与“{query}”相关的内容
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">试试球队名或联赛名称</p>
                  </div>
                ) : (
                  results.map((r) => (
                    <button
                      key={`${r.type}-${r.key}`}
                      type="button"
                      onClick={() => handleGo(r.to)}
                      className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs shrink-0">
                        <i
                          className={
                            r.type === "比赛"
                              ? "ri-basketball-line"
                              : r.type === "球队"
                              ? "ri-shield-line"
                              : "ri-newspaper-line"
                          }
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-foreground-900 truncate">{r.label}</span>
                        <span className="block text-xs text-foreground-500">{r.sub}</span>
                      </span>
                      <span className="text-[10px] font-label text-secondary-700 bg-secondary-100 px-1.5 py-0.5 rounded shrink-0">
                        {r.type}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-background-50 hover:bg-primary-800 transition-colors cursor-pointer"
            aria-label="打开菜单"
          >
            <i className="ri-menu-line text-2xl" />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute right-0 top-0 h-full w-[78%] max-w-xs bg-background-50 flex flex-col">
            <div className="flex items-center justify-between h-16 px-5 bg-primary-950">
              <span className="font-heading font-semibold text-background-50">篮球直播</span>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md text-background-50 hover:bg-primary-800 cursor-pointer"
                aria-label="关闭菜单"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>
            <div className="p-4">
              <form onSubmit={handleSubmit} className="relative mb-3">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="搜索赛事、球队、资讯"
                  className="w-full bg-background-100 border border-background-200 rounded-full pl-9 pr-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                />
              </form>
              {query.trim() !== "" && (
                <div className="mb-3">
                  {results.length === 0 ? (
                    <p className="text-sm text-foreground-500 px-1 py-2">未找到相关内容</p>
                  ) : (
                    results.slice(0, 6).map((r) => (
                      <button
                        key={`m-${r.type}-${r.key}`}
                        type="button"
                        onClick={() => handleGo(r.to)}
                        className="w-full text-left px-3 py-2 rounded-md hover:bg-background-100 cursor-pointer"
                      >
                        <span className="block text-sm text-foreground-900 truncate">{r.label}</span>
                        <span className="block text-xs text-foreground-500">{r.sub}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
              <nav className="flex flex-col">
                {[
                  { to: "/", label: "首页" },
                  { to: "/schedule", label: "赛程" },
                  { to: "/news", label: "资讯" },
                  { to: "/about", label: "关于" },
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className="px-3 py-3 rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer"
                  >
                    {item.label}
                  </Link>
                ))}
                <p className="px-3 pt-4 pb-2 text-xs text-foreground-500">赛事分类</p>
                {leagues.map((l) => (
                  <Link
                    key={l.slug}
                    to={`/category/${l.slug}`}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer"
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: l.accent }} />
                    {l.name}
                    <span className="ml-auto text-xs text-foreground-500">{l.region}</span>
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}